import { useEffect, useMemo, useState } from 'react';
import { db } from '../../db/schema.js';
import { createRow, updateRow } from '../../db/mutations.js';
import { useMasterOptions, type ManagedTable } from '../../db/queries.js';
import { Dialog } from '../../components/ui/dialog.js';
import { Button } from '../../components/ui/button.js';
import { Checkbox, Field, Input, Select, Textarea } from '../../components/ui/field.js';
import type { MasterDefinition } from '../../components/layout/navigation.js';

/**
 * Create/edit for any of the eleven master tables.
 *
 * A dialog rather than a page on purpose: master rows are almost always created
 * *while* filling in a mould ("the designer isn't in the list"). Navigating away
 * to a separate screen would lose the half-typed mould behind it.
 */

type Values = Record<string, string | boolean>;

export function MasterFormDialog({
  definition,
  open,
  onOpenChange,
  rowId,
  onCreated,
}: {
  definition: MasterDefinition;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Omit to create. */
  rowId?: string | null;
  /** Fired with the new row's id - lets a parent select what was just created. */
  onCreated?: (id: string) => void;
}) {
  const editing = !!rowId;
  const [values, setValues] = useState<Values>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Load the existing row when editing; reset to blanks when creating.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (!rowId) {
      setValues({ code: '', name: '', isActive: true });
      setLoaded(true);
      return;
    }
    setLoaded(false);
    void db
      .table(definition.table)
      .get(rowId)
      .then((row: Record<string, unknown> | undefined) => {
        const next: Values = {
          code: String(row?.code ?? ''),
          name: String(row?.name ?? ''),
          isActive: row?.isActive !== false,
        };
        for (const f of definition.extraFields ?? []) {
          const v = row?.[f.name];
          next[f.name] = v === null || v === undefined ? '' : String(v);
        }
        setValues(next);
        setLoaded(true);
      });
  }, [open, rowId, definition]);

  function set(name: string, value: string | boolean) {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => (e[name] ? { ...e, [name]: '' } : e));
  }

  async function onSubmit() {
    const code = String(values.code ?? '').trim();
    const name = String(values.name ?? '').trim();

    const nextErrors: Record<string, string> = {};
    if (!code) nextErrors.code = 'Code is required.';
    if (!name) nextErrors.name = 'Name is required.';

    // Codes are the join key for the client's spreadsheets and the engraving
    // exports; a duplicate is checked here rather than discovered at sync time.
    if (code) {
      const clash = (await db
        .table(definition.table)
        .where('code')
        .equalsIgnoreCase(code)
        .first()) as { id: string } | undefined;
      if (clash && clash.id !== rowId) {
        nextErrors.code = `Code "${code}" is already used.`;
      }
    }

    // A store with no location, or a checkpoint with no location, is a row the
    // server will reject at sync time - long after the dialog has closed and the
    // person has moved on. Caught here instead.
    for (const f of definition.extraFields ?? []) {
      if (f.required && !String(values[f.name] ?? '').trim()) {
        nextErrors[f.name] = `${f.label} is required.`;
      }
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    const payload: Record<string, unknown> = { code, name };
    if (definition.hasActiveFlag !== false) payload.isActive = values.isActive !== false;
    for (const f of definition.extraFields ?? []) {
      const raw = values[f.name];
      const str = typeof raw === 'string' ? raw.trim() : raw;
      if (f.type === 'number') {
        payload[f.name] = str === '' || str === undefined ? null : Number(str);
      } else {
        payload[f.name] = str === '' ? null : (str ?? null);
      }
    }

    setSaving(true);
    try {
      if (editing && rowId) {
        await updateRow(definition.table as ManagedTable, rowId, payload);
      } else {
        const { id } = await createRow(definition.table as ManagedTable, payload);
        onCreated?.(id);
      }
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={editing ? `Edit ${definition.singular.toLowerCase()}` : `New ${definition.singular.toLowerCase()}`}
      description={definition.description}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void onSubmit()} loading={saving}>
            {editing ? 'Save changes' : `Create ${definition.singular.toLowerCase()}`}
          </Button>
        </>
      }
    >
      {!loaded ? (
        <div className="py-6 text-center text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="flex flex-col gap-4 pb-2">
          <Field label="Code" required error={errors.code} hint={definition.codeHint}>
            <Input
              value={String(values.code ?? '')}
              onChange={(e) => set('code', e.target.value)}
              placeholder={definition.codePlaceholder ?? 'SHORT-CODE'}
              autoCapitalize="characters"
              autoFocus={!editing}
            />
          </Field>

          <Field label="Name" required error={errors.name}>
            <Input
              value={String(values.name ?? '')}
              onChange={(e) => set('name', e.target.value)}
              placeholder={`${definition.singular} name`}
            />
          </Field>

          {(definition.extraFields ?? []).map((f) => (
            <ExtraField
              key={f.name}
              field={f}
              value={String(values[f.name] ?? '')}
              error={errors[f.name]}
              onChange={(v) => set(f.name, v)}
            />
          ))}

          {definition.hasActiveFlag !== false ? (
            <Checkbox
              checked={values.isActive !== false}
              onChange={(e) => set('isActive', e.target.checked)}
              label="Active"
              description="Inactive rows stay in history but disappear from dropdowns."
            />
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

function ExtraField({
  field,
  value,
  error,
  onChange,
}: {
  field: NonNullable<MasterDefinition['extraFields']>[number];
  value: string;
  error?: string;
  onChange: (value: string) => void;
}) {
  const linked = useMasterOptions((field.optionsFrom ?? 'companies') as ManagedTable);
  const options = useMemo(
    () => (field.optionsFrom ? (linked ?? []) : (field.options ?? [])),
    [field.optionsFrom, field.options, linked],
  );

  if (field.type === 'textarea') {
    return (
      <Field label={field.label} hint={field.hint} error={error}>
        <Textarea value={value} onChange={(e) => onChange(e.target.value)} rows={4} />
      </Field>
    );
  }
  if (field.type === 'select') {
    return (
      <Field label={field.label} hint={field.hint} error={error} required={field.required}>
        <Select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Not set"
          options={options}
        />
      </Field>
    );
  }
  return (
    <Field label={field.label} hint={field.hint} error={error} required={field.required}>
      <Input
        type={field.type === 'number' ? 'number' : 'text'}
        step={field.type === 'number' ? 'any' : undefined}
        inputMode={field.type === 'number' ? 'decimal' : undefined}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
      />
    </Field>
  );
}
